/**
 * Review types, eligible study designs and their names (F07 EVM-170 to EVM-174): one list and
 * one set of words for the question builder, the prompts, the protocol document and the page.
 *
 * A question keeps the four Pico keys whatever its review type; only their labels change, so
 * every stored question, page, screening prompt and export keeps working.
 *
 * Implements EVM-170, EVM-171, EVM-172, EVM-173 and EVM-174.
 */
import type { OutcomeType, Pico, QuestionStudyDesign, ReviewType, StudyDesign } from './types';
import { isRandomisedDesign } from './types';

/** EVM-170: in the order the builder offers them. */
export const REVIEW_TYPES: readonly ReviewType[] = Object.freeze(['intervention', 'exposure', 'prevalence']);

/** EVM-171: the designs of EVM-140, randomised first. */
export const STUDY_DESIGNS: readonly StudyDesign[] = Object.freeze([
  'rct',
  'cluster_rct',
  'crossover_rct',
  'non_randomised_trial',
  'cohort',
  'case_control',
  'cross_sectional',
  'single_arm',
  'case_series',
]);

export interface DesignName {
  /** "Cohort study", as a checkbox or a value is labelled. */
  singular: string;
  /** "cohort studies", lower case, for running text and lists. */
  plural: string;
}

/** EVM-171. */
export const STUDY_DESIGN_NAMES: Readonly<Record<StudyDesign, DesignName>> = Object.freeze({
  rct: { singular: 'Randomised controlled trial', plural: 'randomised controlled trials' },
  cluster_rct: { singular: 'Cluster-randomised trial', plural: 'cluster-randomised trials' },
  crossover_rct: { singular: 'Crossover randomised trial', plural: 'crossover randomised trials' },
  non_randomised_trial: { singular: 'Non-randomised controlled trial', plural: 'non-randomised controlled trials' },
  cohort: { singular: 'Cohort study', plural: 'cohort studies' },
  case_control: { singular: 'Case-control study', plural: 'case-control studies' },
  cross_sectional: { singular: 'Cross-sectional study', plural: 'cross-sectional studies' },
  single_arm: { singular: 'Single-arm study', plural: 'single-arm studies' },
  case_series: { singular: 'Case series', plural: 'case series' },
});

export interface ReviewTypeName {
  /** "Exposure or risk factor". */
  name: string;
  /** "PECO". */
  framing: string;
}

/** EVM-170. */
export const REVIEW_TYPE_NAMES: Readonly<Record<ReviewType, ReviewTypeName>> = Object.freeze({
  intervention: { name: 'Effects of an intervention', framing: 'PICO' },
  exposure: { name: 'Exposure or risk factor', framing: 'PECO' },
  prevalence: { name: 'Prevalence or incidence', framing: 'condition, context, population' },
});

export type PicoLabels = Readonly<Record<keyof Pico, string>>;

const PICO_LABELS: Readonly<Record<ReviewType, PicoLabels>> = Object.freeze({
  intervention: { population: 'Population', intervention: 'Intervention', comparator: 'Comparator', outcome: 'Outcome' },
  exposure: { population: 'Population', intervention: 'Exposure', comparator: 'Comparator', outcome: 'Outcome' },
  prevalence: { population: 'Population', intervention: 'Condition', comparator: 'Context', outcome: 'Outcome measure' },
});

/** EVM-170: the labels of the four question fields for a review type. */
export function picoLabels(reviewType: ReviewType): PicoLabels {
  return PICO_LABELS[reviewType];
}

/** EVM-171: unique, in STUDY_DESIGNS order. Unknown values are dropped. */
export function normaliseDesigns(designs: readonly string[]): StudyDesign[] {
  const wanted = new Set(designs);
  return STUDY_DESIGNS.filter((d) => wanted.has(d));
}

const DEFAULT_DESIGNS: Readonly<Record<ReviewType, readonly StudyDesign[]>> = Object.freeze({
  intervention: Object.freeze(['rct'] as StudyDesign[]),
  exposure: Object.freeze(['cohort', 'case_control'] as StudyDesign[]),
  prevalence: Object.freeze(['cohort', 'cross_sectional'] as StudyDesign[]),
});

/** EVM-171: the designs that usually answer each kind of question. A fresh copy. */
export function defaultDesignsFor(reviewType: ReviewType): StudyDesign[] {
  return normaliseDesigns(DEFAULT_DESIGNS[reviewType]);
}

/** EVM-172. An empty list counts as randomised, the v1 meaning. */
export function designClass(designs: readonly StudyDesign[]): QuestionStudyDesign {
  const randomised = designs.filter(isRandomisedDesign).length;
  if (randomised === designs.length) return 'rct';
  return randomised === 0 ? 'non_randomised' : 'mixed';
}

/** EVM-173: an absent review type is an intervention review. */
export function questionReviewType(question: { reviewType?: ReviewType | undefined }): ReviewType {
  return question.reviewType ?? 'intervention';
}

/** EVM-173: absent eligible designs are randomised controlled trials. */
export function questionDesigns(question: { studyDesigns?: readonly StudyDesign[] | undefined }): StudyDesign[] {
  return question.studyDesigns && question.studyDesigns.length ? normaliseDesigns(question.studyDesigns) : ['rct'];
}

const OUTCOME_TYPES_BY_REVIEW: Readonly<Record<ReviewType, readonly OutcomeType[]>> = Object.freeze({
  intervention: Object.freeze(['binary', 'continuous', 'time_to_event', 'proportion', 'rate'] as OutcomeType[]),
  exposure: Object.freeze(['binary', 'continuous', 'time_to_event', 'rate', 'correlation'] as OutcomeType[]),
  prevalence: Object.freeze(['proportion', 'rate'] as OutcomeType[]),
});

/** EVM-174: the outcome types a review type can use, in the order the builder offers them. */
export function outcomeTypesFor(reviewType: ReviewType): readonly OutcomeType[] {
  return OUTCOME_TYPES_BY_REVIEW[reviewType];
}

/** "cohort studies, case-control studies": the plural names, comma-separated, in STUDY_DESIGNS order. */
export function designList(designs: readonly StudyDesign[]): string {
  return normaliseDesigns(designs)
    .map((d) => STUDY_DESIGN_NAMES[d].plural)
    .join(', ');
}
